import { WarningCircleSolid, Xmark } from '@byway/icons';
import { useLocale, useTranslations } from 'next-intl';
import { Image } from 'react-datocms/image';
import { Button } from '~/components/Button';
import { Card } from '~/components/Card';
import { Heading } from '~/components/Heading';
import { Stack } from '~/components/Layout/Stack';
import { useToasts } from '~/components/Toast';
import { api } from '~/libs/api';
import type { Place } from '~/libs/dato/queries/places';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundContextParams, usePlaygroundPlaceAiSuggestions, usePlaygroundUIState } from '~/stores/playgroundStore';
import type { StayTransformed } from '~/types/JourneyItem.types';
import type { PlaygroundContext } from '~/types/Playground.types';
import { gtag, trackEvent } from '~/utils';
import { formatDayRanges, getProcessedDescription } from '../mapHelpers';
import styles from './PlaceCard.module.css';
type PlaceCardProps = {
  place: Place['place'] & {
    suggestions?: string;
  };
  close: () => void;
  isAdded?: boolean;
  isReachable: boolean;
  variant?: 'build' | 'review';
  stayItems?: StayTransformed[];
  tripStartDate?: string;
};
export function PlaceCard({
  place,
  close,
  isAdded = false,
  isReachable,
  variant = 'build',
  stayItems,
  tripStartDate
}: PlaceCardProps) {
  const addDestinationCall = api.playground.addDestination.useMutation();
  const removeDestinationCall = api.playground.removeDestination.useMutation();
  const playgroundContext = usePlaygroundContext();
  const {
    removingDestination,
    isAddingDestination
  } = usePlaygroundUIState();
  const {
    setContext,
    addDestination,
    setPlaceDialog: setActiveDialog,
    setLastAddedDestination,
    setIsAddingDestination,
    setRemovingDestination,
    openDrawer
  } = usePlaygroundActions();
  const {
    chosenDestination,
    chosenOrigin,
    startDate
  } = usePlaygroundContextParams();
  const hasStartDate = typeof startDate !== 'undefined' && startDate !== null;
  const toasts = useToasts();
  const t = useTranslations();
  const locale = useLocale();
  const hasErrors = removeDestinationCall.error || addDestinationCall.error;
  const isMobile = window && window.innerWidth < 768;
  const isEndPlace = chosenDestination ? chosenDestination.id === place.placeId : false;
  const isStartPlace = chosenOrigin.id === place.placeId;
  const description = place?.playgroundDescription || place?.description;

  // Get stored AI suggestions for this place if it's already added
  const storedAiSuggestions = usePlaygroundPlaceAiSuggestions(place.placeId ?? undefined);

  // Use stored suggestions if available, otherwise use the place suggestions
  const effectiveSuggestions = storedAiSuggestions || place.suggestions;
  const handleRemovePlace = async () => {
    if (!place?.placeId) {
      return;
    }
    try {
      setRemovingDestination(new Set([place?.placeId]));
      const newContext = await removeDestinationCall.mutateAsync({
        playgroundInputContext: playgroundContext as PlaygroundContext,
        placeId: place?.placeId
      });
      gtag('event', 'destination_unpinned', {
        event_category: 'Playground',
        event_label: place?.placeName,
        destination_id: place?.placeId
      });
      // TODO - error and  Loading states / styles)
      if (!newContext) {
        setRemovingDestination(new Set());
        return;
      }
      setContext(newContext);
      setRemovingDestination(new Set());
      close();
    } catch (_error) {
      setRemovingDestination(new Set());
    }
  };
  const handleAddPlace = async () => {
    if (!place?.placeId) {
      return;
    }
    try {
      if (!hasStartDate) {
        toasts.add({
          title: t('content.feedback.dates_select_title'),
          description: t('content.feedback.dates_select_description'),
          variant: 'info',
          position: isMobile ? 'top' : 'bottom'
        }, {
          timeout: 5000
        });
        setActiveDialog(null);
        openDrawer('dates');
        return;
      }
      setIsAddingDestination(true);
      const res = await addDestinationCall.mutateAsync({
        playgroundInputContext: playgroundContext as PlaygroundContext,
        // TODO fix this, typing context almost there
        placeId: place.placeId,
        countryId: place.country.countryId,
        userSpecificDescription: effectiveSuggestions && typeof effectiveSuggestions === 'string' && effectiveSuggestions.trim().length > 0 ? effectiveSuggestions.trim() : null
      });

      // Check if place is AI generated and fire specific GTM tag
      if (place.suggestions) {
        trackEvent('ga4_playground_ai_destination_pinned', {
          event_category: 'Playground',
          event_label: 'ga4_playground_ai_destination_pinned',
          place_name: place.placeName
        });
      } else {
        gtag('event', 'destination_pinned', {
          event_category: 'Playground',
          event_label: place.placeName,
          destination_id: place.placeId
        });
      }
      if (!res) {
        setIsAddingDestination(false);
        return;
      }
      setContext(res);
      // Add a small delay to let the context update
      setTimeout(() => {
        const currentState = sessionStorage.getItem('playground-state');
        if (currentState) {
          const _parsed = JSON.parse(currentState);
        }
      }, 100);
      addDestination(place.placeId, effectiveSuggestions && typeof effectiveSuggestions === 'string' && effectiveSuggestions.trim().length > 0 ? effectiveSuggestions.trim() : undefined);
      setLastAddedDestination(place.placeId);
      setIsAddingDestination(false);
      close();
    } catch (_error_0) {
      setIsAddingDestination(false);
    }
  };
  const handleChangeStartEnd = () => {
    close();
    openDrawer('start-end');
  };
  const isDisabled = isAddingDestination || (removingDestination?.size ?? 0) > 0;
  const PlaceCardButton = () => {
    const placeId = place?.placeId;
    const isRemoving = typeof placeId === 'number' && removingDestination?.has(placeId);
    if (isStartPlace) return <Button variant="primary" isLoading={isRemoving} onPress={handleChangeStartEnd}>
          {t('common.change_origin')}
        </Button>;
    if (isEndPlace) return <Button variant="primary" isLoading={isRemoving} onPress={handleChangeStartEnd}>
          {t('playground.destination_change')}
        </Button>;
    if (isAdded) return <Button isDisabled={isDisabled} isLoading={isRemoving} onPress={handleRemovePlace} variant="tertiary" data-ua-click="playground-destination-remove">
          {t('playground.place_remove')}
        </Button>;
    if (!isReachable) return <Button isDisabled variant="secondary">
          {t('content.feedback.unreachable')}
        </Button>;
    if (!isAdded && isReachable) return <Button isDisabled={isDisabled} isLoading={isAddingDestination} onPress={handleAddPlace} variant="secondary" data-ua-click="playground-destination-add">
          {t('playground.place_add')}
        </Button>;
    return <Button isDisabled variant="secondary">
        {t('content.feedback.place_add_error')}
      </Button>;
  };
  return <div className={styles.container}>
      <Card isElevated fullHeight rounded="--by-border-radius-300" isFlex>
        <span className={styles.closeButton}>
          <Button variant="icon" ariaLabel="close" onPress={close}>
            <Xmark decorative size="large" />
          </Button>
        </span>
        {place?.images[0]?.responsiveImage ? <Image data={place.images[0].responsiveImage} className={styles.image} objectFit="cover" layout="responsive" /> : <div>No image to show</div>}
        <div className={styles.contentWrapper}>
          {variant === 'review' && stayItems && stayItems.length > 0 && <div className={styles.dayRange}>{formatDayRanges(stayItems, tripStartDate)}</div>}
          <Stack space="--by-space-100">
            {/* TODO: heading probably not the thing to use - but caption styles deprecated */}
            <Heading level="h3" variant="heading-xs">
              <div className={styles.title}>
                <span>{place?.placeName}, </span>
                <span className={styles.subtitle}>{place?.country.countryName}</span>
              </div>
            </Heading>
          </Stack>
          <div className={styles.ctaWrapper}>
            <div className={styles.contentArea}>
              <div className={styles.description}>
                {getProcessedDescription(
              // The AI Travel Planner webhook always generates this text in English,
              // regardless of chatInput language - only prefer it over the localized CMS
              // description when the UI is displaying in English.
              locale.startsWith('en') ? effectiveSuggestions || undefined : undefined, description || undefined)}
              </div>
              {hasErrors && <div className={styles.errorsContainer}>
                  {addDestinationCall.error && <p className={styles.error}>
                      <WarningCircleSolid decorative />
                      {t('content.feedback.destination_add_error')}
                    </p>}
                  {removeDestinationCall.error && <p className={styles.error}>
                      <WarningCircleSolid decorative />
                      {t('content.feedback.destination_remove_error')}
                    </p>}
                </div>}
            </div>
            {variant === 'build' && <PlaceCardButton />}
          </div>
        </div>
      </Card>
    </div>;
}